import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { User, Session } from '@supabase/supabase-js';
import type { AdminPermissions, AdminRole } from '@/types/admin';
import { supabase } from '@/services/supabase';
import { getAdminAuth } from '@/services/api';
import { withTimeout, TimeoutError } from '@/utils/promiseUtils';

interface AuthState {
  user: User | null;
  session: Session | null;
  isAdmin: boolean;
  role: AdminRole | null;
  permissions: AdminPermissions | null;
  adminUserId: string | null;
  isLoading: boolean;
  error: string | null;
  lastActivityAt: number;
  lastActivity: number; // Alias for SessionTimeoutWarning component

  // Actions
  initialize: () => Promise<void>;
  signIn: (email: string, password: string) => Promise<void>;
  signOut: () => Promise<void>;
  refreshAdminStatus: () => Promise<void>;
  hasPermission: (category: keyof AdminPermissions, action: string) => boolean;
  updateLastActivity: () => void;
  checkSessionTimeout: () => boolean;
}

const SESSION_TIMEOUT_MS = 30 * 60 * 1000; // 30 minutes
const SESSION_CHECK_TIMEOUT_MS = 10000; // 10 seconds for session check
const ADMIN_AUTH_TIMEOUT_MS = 15000; // 15 seconds for admin auth check

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      user: null,
      session: null,
      isAdmin: false,
      role: null,
      permissions: null,
      adminUserId: null,
      isLoading: true,
      error: null,
      lastActivityAt: Date.now(),
      lastActivity: Date.now(),

      initialize: async () => {
        const initStartTime = Date.now();
        try {
          set({ isLoading: true, error: null });

          // Get current session with timeout to prevent infinite loading
          let session = null;
          let sessionError = null;

          try {
            const result = await withTimeout(
              supabase.auth.getSession(),
              SESSION_CHECK_TIMEOUT_MS,
              'getSession'
            );
            session = result.data.session;
            sessionError = result.error;
          } catch (error) {
            if (error instanceof TimeoutError) {
              console.error('Session check timed out');
              set({ user: null, session: null, isAdmin: false, isLoading: false, error: 'Session check timed out. Please refresh.' });
              return;
            }
            throw error;
          }

          if (sessionError) throw sessionError;

          if (!session) {
            set({ user: null, session: null, isAdmin: false, isLoading: false });
            return;
          }

          set({ user: session.user, session });

          // Check admin status with timeout
          try {
            const adminAuth = await withTimeout(
              getAdminAuth(),
              ADMIN_AUTH_TIMEOUT_MS,
              'getAdminAuth'
            );
            set({
              isAdmin: adminAuth.isAdmin,
              role: adminAuth.role,
              permissions: adminAuth.permissions,
              adminUserId: adminAuth.adminUserId,
            });
          } catch (error) {
            if (error instanceof TimeoutError) {
              console.error('Admin auth check timed out');
            }
            // Not an admin or auth failed - still allow loading to complete
            set({ isAdmin: false, role: null, permissions: null });
          }

          // CRITICAL: Always set isLoading to false
          set({ isLoading: false });
          console.log(`Admin console initialized in ${Date.now() - initStartTime}ms`);

          // Listen for auth changes
          supabase.auth.onAuthStateChange(async (event, newSession) => {
            if (event === 'SIGNED_OUT') {
              set({
                user: null,
                session: null,
                isAdmin: false,
                role: null,
                permissions: null,
                adminUserId: null,
              });
            } else if (newSession) {
              set({ user: newSession.user, session: newSession });
              // Refresh admin status in background - don't await to prevent blocking
              get().refreshAdminStatus().catch(console.error);
            }
          });
        } catch (error) {
          console.error('Auth initialization error:', error);
          set({
            error: error instanceof Error ? error.message : 'Failed to initialize auth',
            isLoading: false, // CRITICAL: Always set to false even on error
          });
        }
      },

      signIn: async (email: string, password: string) => {
        try {
          set({ isLoading: true, error: null });

          const { data, error } = await supabase.auth.signInWithPassword({
            email,
            password,
          });

          if (error) throw error;

          set({ user: data.user, session: data.session });

          // Check admin status
          const adminAuth = await getAdminAuth();

          if (!adminAuth.isAdmin) {
            // Sign out if not an admin
            await supabase.auth.signOut();
            throw new Error('Access denied. Admin privileges required.');
          }

          set({
            isAdmin: adminAuth.isAdmin,
            role: adminAuth.role,
            permissions: adminAuth.permissions,
            adminUserId: adminAuth.adminUserId,
            isLoading: false,
            lastActivityAt: Date.now(),
          });
        } catch (error) {
          set({
            error: error instanceof Error ? error.message : 'Sign in failed',
            isLoading: false,
          });
          throw error;
        }
      },

      signOut: async () => {
        try {
          await supabase.auth.signOut();
          set({
            user: null,
            session: null,
            isAdmin: false,
            role: null,
            permissions: null,
            adminUserId: null,
          });
        } catch (error) {
          console.error('Sign out error:', error);
        }
      },

      refreshAdminStatus: async () => {
        try {
          const adminAuth = await getAdminAuth();
          set({
            isAdmin: adminAuth.isAdmin,
            role: adminAuth.role,
            permissions: adminAuth.permissions,
            adminUserId: adminAuth.adminUserId,
          });
        } catch {
          set({ isAdmin: false, role: null, permissions: null });
        }
      },

      hasPermission: (category: keyof AdminPermissions, action: string): boolean => {
        const { permissions, role } = get();

        // Super admin has all permissions
        if (role === 'super_admin') return true;

        if (!permissions) return false;

        const categoryPermissions = permissions[category];
        return Array.isArray(categoryPermissions) && categoryPermissions.includes(action);
      },

      updateLastActivity: () => {
        const now = Date.now();
        set({ lastActivityAt: now, lastActivity: now });
      },

      checkSessionTimeout: (): boolean => {
        const { lastActivityAt, session } = get();
        if (!session) return false;

        const now = Date.now();
        const elapsed = now - lastActivityAt;

        return elapsed > SESSION_TIMEOUT_MS;
      },
    }),
    {
      name: 'admin-auth-storage',
      partialize: (state) => ({
        lastActivityAt: state.lastActivityAt,
      }),
    }
  )
);
